/**
 * Development tools for debugging NaN issues and CoreGraphics problems
 */
import React from 'react';

/**
 * Install console traps to catch NaN values being set as CSS properties
 * This helps identify where NaN values are reaching the rendering layer
 */
export const installNaNTraps = () => {
  if (process.env.NODE_ENV !== 'development') return;

  console.log('[DevTools] Installing NaN detection traps...');

  // Trap CSS property setting to catch NaN values
  const originalSetProperty = CSSStyleDeclaration.prototype.setProperty;
  CSSStyleDeclaration.prototype.setProperty = function(property: string, value: string, priority?: string) {
    if (value && /NaN/.test(String(value))) {
      console.warn('[NaN Trap] NaN detected in CSS property:', {
        property,
        value,
        element: this,
        stack: new Error().stack
      });
    }
    return originalSetProperty.call(this, property, value, priority);
  };

  // Trap canvas context operations that might receive NaN
  const trapCanvasMethod = (prototype: any, methodName: string) => {
    const original = prototype[methodName];
    if (typeof original === 'function') {
      prototype[methodName] = function(...args: any[]) {
        const hasNaN = args.some(arg => typeof arg === 'number' && !Number.isFinite(arg));
        if (hasNaN) {
          console.warn(`[NaN Trap] NaN detected in canvas.${methodName}:`, args);
        }
        return original.apply(this, args);
      };
    }
  };

  // Trap common canvas methods
  if (typeof CanvasRenderingContext2D !== 'undefined') {
    ['drawImage', 'fillRect', 'strokeRect', 'arc', 'lineTo', 'moveTo', 'scale', 'translate', 'rotate']
      .forEach(method => trapCanvasMethod(CanvasRenderingContext2D.prototype, method));
  }

  console.log('[DevTools] NaN traps installed successfully');
};

/**
 * Boot proof indicator to confirm WebView is rendering
 */
export const BootProofIndicator = () => {
  if (process.env.NODE_ENV !== 'development') return null;

  return React.createElement('div', {
    style: {
      position: 'fixed',
      bottom: 8,
      right: 8,
      opacity: 0.6,
      fontSize: 12,
      backgroundColor: '#00ff00',
      color: '#000',
      padding: '2px 6px',
      borderRadius: 4,
      zIndex: 9999,
      fontFamily: 'monospace'
    }
  }, 'boot ok');
};

/**
 * Enhanced console logging for debugging calculations
 */
export const debugCalc = (label: string, calculation: () => number, fallback = 0): number => {
  if (process.env.NODE_ENV !== 'development') {
    try {
      const result = calculation();
      return Number.isFinite(result) ? result : fallback;
    } catch {
      return fallback;
    }
  }

  try {
    const result = calculation();
    if (!Number.isFinite(result)) {
      console.warn(`[DebugCalc] Non-finite result for ${label}:`, result);
      return fallback;
    }
    return result;
  } catch (error) {
    console.warn(`[DebugCalc] Error in calculation ${label}:`, error);
    return fallback;
  }
};